import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import ProductImage from '../components/ProductImage';
import API_BASE_URL from '../config/apiConfig';
import countryOptions from '../constants/countryOptions';

const ShopCheckout = () => {
  const { cartItems, cartTotal, clearCart } = useCart();
    const { user, token, isAuthenticated } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
    const [formError, setFormError] = useState('');
        const [orderAddressMessage, setOrderAddressMessage] = useState('');
        const [saveBillingAddress, setSaveBillingAddress] = useState(true);
        const [paymentOption, setPaymentOption] = useState('cod');
    const [placedOrderInfo, setPlacedOrderInfo] = useState(null);
  const [savedAddressId, setSavedAddressId] = useState(null);
    const [addressLoading, setAddressLoading] = useState(false);
  const [formData, setFormData] = useState({
        fname: '',
        lname: '',
    email: '',
    phone: '',
        billing_address: '',
        billing_address2: '',
    billing_country: '',
    city: '',
        zipcode: '',
    shipping_country: '',
    notes: '',
  });

    const fetchSavedAddress = useCallback(async () => {
        if (!isAuthenticated || !token) return;
        setAddressLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/ec/address/getByUser?type=billing`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const result = await response.json();
            if (response.ok && result?.success && result?.data?.length > 0) {
                const defaultAddr = result.data.find((a) => a.IsDefault) || result.data[0];
                setSavedAddressId(defaultAddr.AddressId || null);
                setFormData((prev) => ({
                    ...prev,
                    fname: defaultAddr.FirstName || '',
                    lname: defaultAddr.LastName || '',
                    phone: defaultAddr.Phone || '',
                    billing_address: defaultAddr.AddressLine1 || '',
                    billing_address2: defaultAddr.AddressLine2 || '',
                    billing_country: defaultAddr.Country || '',
                    city: defaultAddr.City || '',
                    zipcode: defaultAddr.Zipcode || '',
                    email: user?.Email || prev.email,
                }));
            } else if (user) {
                setFormData((prev) => ({
                    ...prev,
                    fname: user.FirstName || prev.fname,
                    lname: user.LastName || prev.lname,
                    email: user.Email || prev.email,
                }));
            }
        } catch (_) {
            // Silently fail — user can still fill the form manually
        } finally {
            setAddressLoading(false);
        }
    }, [isAuthenticated, token, user]);

    useEffect(() => {
        fetchSavedAddress();
    }, [fetchSavedAddress]);

  const handleSelectChange = (fieldName, selectedOption) => {
    setFormData((previous) => ({
      ...previous,
      [fieldName]: selectedOption ? selectedOption.value : '',
    }));
  };

    const cartCount = cartItems.reduce((accumulator, item) => accumulator + Number(item.quantity || 0), 0);

    const formatCurrency = (value) => {
        const parsedValue = Number(value);
        if (!Number.isFinite(parsedValue)) {
            return value;
        }
        return parsedValue.toLocaleString('en-IN');
    };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

    const handlePlaceOrder = async (event) => {
    event.preventDefault();
        setFormError('');

        if (!isAuthenticated || !user?.UserId || !token) {
            setFormError('Please login to place your order.');
            return;
        }

    if (!cartItems.length) {
            setFormError('Your cart is empty. Please add at least one product before placing an order.');
      return;
    }

        if (paymentOption !== 'cod') {
            setFormError('Only Cash on Delivery is available right now. Please select Cash on delivery to continue.');
            return;
        }

        if (!formData.fname || !formData.email || !formData.phone || !formData.billing_address || !formData.city || !formData.zipcode) {
                        setFormError('Please fill all required billing details before placing your order.');
            return;
        }

    setIsSubmitting(true);
        try {
            const response = await fetch(`${API_BASE_URL}/ec/order/place`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    paymentMethod: paymentOption,
                    billingDetails: formData,
                    cartItems,
                    totalAmount: cartTotal,
                }),
            });

            const result = await response.json();

            if (!response.ok || !result?.success) {
                setFormError(result?.message || 'Unable to place order. Please try again.');
                return;
            }

            if (saveBillingAddress) {
                try {
                    const addressResponse = await fetch(`${API_BASE_URL}/ec/address/save`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                            AddressId: savedAddressId || 0,
                            AddressType: 'billing',
                            FirstName: formData.fname,
                            LastName: formData.lname,
                            Phone: formData.phone,
                            AddressLine1: formData.billing_address,
                            AddressLine2: formData.billing_address2,
                            City: formData.city,
                            Country: formData.billing_country,
                            Zipcode: formData.zipcode,
                            IsDefault: savedAddressId ? true : false,
                        }),
                    });

                    const addressResult = await addressResponse.json();
                    if (addressResponse.ok && addressResult?.success) {
                        setOrderAddressMessage('Billing address saved to My Address.');
                    } else {
                        setOrderAddressMessage(addressResult?.message || 'Order placed, but address was not saved.');
                    }
                } catch (_) {
                    setOrderAddressMessage('Order placed, but address was not saved due to network issue.');
                }
            } else {
                setOrderAddressMessage('');
            }

            setPlacedOrderInfo(result?.data || null);
      setOrderPlaced(true);
      clearCart();
        } catch (_) {
            setFormError('Unable to reach server. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
  };

  if (!cartItems.length && !orderPlaced) {
    return (
      <div className="container mb-30 mt-50">
        <div className="row justify-content-center">
          <div className="col-lg-6 text-center">
            <h5 className="mb-20">Your cart is empty. Add products before checkout.</h5>
            <Link className="btn" to="/">
              <i className="fi-rs-arrow-left mr-10"></i>
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (orderPlaced) {
    return (
      <div className="container mb-30 mt-50">
        <div className="row justify-content-center">
          <div className="col-lg-6 text-center">
            <h2 className="text-brand mb-15">Order placed successfully!</h2>
            <p className="mb-25">Thank you for shopping with SK Cart.</p>
                        {placedOrderInfo?.OrderNumber ? <p className="mb-10">Order Number: <strong>{placedOrderInfo.OrderNumber}</strong></p> : null}
                        {placedOrderInfo?.PaymentMethod ? <p className="mb-25 text-muted">Payment Method: {String(placedOrderInfo.PaymentMethod).toUpperCase()}</p> : null}
                                                {orderAddressMessage ? <p className="mb-25 text-muted">{orderAddressMessage}</p> : null}
            <Link className="btn" to="/">
              <i className="fi-rs-arrow-left mr-10"></i>
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mb-80 mt-50">
            <div className="row">
                <div className="col-lg-8 mb-40">
                    <h1 className="heading-2 mb-10">Checkout</h1>
                    <div className="d-flex justify-content-between">
                        <h6 className="text-body">There are <span className="text-brand">{cartCount}</span> products in your cart</h6>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-lg-7">
                    <div className="row mb-50">
                        <div className="col-lg-6 mb-sm-15 mb-lg-0 mb-md-3">
                            <div className="toggle_info">
                                <span><i className="fi-rs-user mr-10"></i><span className="text-muted font-lg">Already have an
                                        account?</span> <a href="#loginform" data-bs-toggle="collapse"
                                        className="collapsed font-lg" aria-expanded="false">Click here to login</a></span>
                            </div>
                            <div className="panel-collapse collapse login_form" id="loginform">
                                <div className="panel-body">
                                    <p className="mb-30 font-sm">If you have shopped with us before, please enter your
                                        details below. If you are a new customer, please proceed to the Billing &amp;
                                        Shipping section.</p>
                                    <form method="post">
                                        <div className="form-group">
                                            <input type="text" name="email" placeholder="Username Or Email"/>
                                        </div>
                                        <div className="form-group">
                                            <input type="password" name="password" placeholder="Password"/>
                                        </div>
                                        <div className="login_footer form-group">
                                            <div className="chek-form">
                                                <div className="custome-checkbox">
                                                    <input className="form-check-input" type="checkbox" name="checkbox"
                                                        id="remember" value=""/>
                                                    <label className="form-check-label" htmlFor="remember"><span>Remember
                                                            me</span></label>
                                                </div>
                                            </div>
                                            <Link to="/password-reset">Forgot password?</Link>
                                        </div>
                                        <div className="form-group">
                                            <button className="btn btn-md" name="login">Log in</button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <form method="post" className="apply-coupon">
                                <input type="text" placeholder="Enter Coupon Code..."/>
                                <button className="btn  btn-md" name="login">Apply Coupon</button>
                            </form>
                        </div>
                    </div>
                    <div className="row">
                        <h4 className="mb-30">Billing Details</h4>
                        {addressLoading && <p className="text-muted mb-15"><i className="fi-rs-spinner mr-5"></i>Loading saved address...</p>}
                        {!addressLoading && savedAddressId && <p className="text-success mb-15"><i className="fi-rs-check mr-5"></i>Your saved billing address has been loaded. You can update the details below.</p>}
                        <form id="checkout-form" method="post" onSubmit={handlePlaceOrder}>
                            <div className="row">
                                <div className="form-group col-lg-6">
                                    <input type="text" required="" name="fname" placeholder="First name *" value={formData.fname} onChange={handleChange}  />
                                </div>
                                <div className="form-group col-lg-6">
                                    <input type="text" required="" name="lname" placeholder="Last name *" value={formData.lname} onChange={handleChange} />
                                </div>
                            </div>
                            <div className="row">
                                <div className="form-group col-lg-6">
                                    <input type="text" name="billing_address" required="" placeholder="Address *" value={formData.billing_address} onChange={handleChange} />
                                </div>
                                <div className="form-group col-lg-6">
                                    <input type="text" name="billing_address2" required="" placeholder="Address line2" value={formData.billing_address2} onChange={handleChange} />
                                </div>
                            </div>
                            <div className="row shipping_calculator">
                                <div className="form-group col-lg-6">
                                    <div className="custom_select">
                                        <Select
                                            className="checkout-react-select"
                                            classNamePrefix="checkout-select"
                                            options={countryOptions}
                                            isSearchable
                                            placeholder="Select an option..."
                                            value={countryOptions.find((option) => option.value === formData.billing_country) || null}
                                            onChange={(selectedOption) => handleSelectChange('billing_country', selectedOption)}
                                        />
                                    </div>
                                </div>
                                <div className="form-group col-lg-6">
                                    <input required="" type="text" name="city" placeholder="City / Town *" value={formData.city} onChange={handleChange} />
                                </div>
                            </div>
                            <div className="row">
                                <div className="form-group col-lg-6">
                                    <input required="" type="text" name="zipcode" placeholder="Postcode / ZIP *" value={formData.zipcode} onChange={handleChange} />
                                </div>
                                <div className="form-group col-lg-6">
                                    <input required="" type="text" name="phone" placeholder="Phone *" value={formData.phone} onChange={handleChange} />
                                </div>
                            </div>
                            <div className="row">
                                <div className="form-group col-lg-6">
                                    <input required="" type="text" name="cname" placeholder="Company Name"/>
                                </div>
                                <div className="form-group col-lg-6">
                                    <input required="" type="text" name="email" placeholder="Email address *" value={formData.email} onChange={handleChange} />
                                </div>
                            </div>
                            <div className="form-group mb-30">
                                <textarea rows="5" name="notes" value={formData.notes} onChange={handleChange} placeholder="Additional information"></textarea>
                            </div>
                            <div className="form-group mb-20">
                                <div className="custome-checkbox">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="saveBillingAddress"
                                        checked={saveBillingAddress}
                                        onChange={(event) => setSaveBillingAddress(event.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="saveBillingAddress">
                                        <span>Save this billing address to My Address</span>
                                    </label>
                                </div>
                            </div>
                            <div className="form-group">
                                <div className="checkbox">
                                    <div className="custome-checkbox">
                                        <input className="form-check-input" type="checkbox" name="checkbox"
                                            id="createaccount"/>
                                        <label className="form-check-label label_info" data-bs-toggle="collapse"
                                            href="#collapsePassword" data-target="#collapsePassword"
                                            aria-controls="collapsePassword" htmlFor="createaccount"><span>Create an
                                                account?</span></label>
                                    </div>
                                </div>
                            </div>
                            <div id="collapsePassword" className="form-group create-account collapse in">
                                <div className="row">
                                    <div className="col-lg-6">
                                        <input required="" type="password" placeholder="Password" name="password"/>
                                    </div>
                                </div>
                            </div>
                            <div className="ship_detail">
                                <div className="form-group">
                                    <div className="chek-form">
                                        <div className="custome-checkbox">
                                            <input className="form-check-input" type="checkbox" name="checkbox"
                                                id="differentaddress"/>
                                            <label className="form-check-label label_info" data-bs-toggle="collapse"
                                                data-target="#collapseAddress" href="#collapseAddress"
                                                aria-controls="collapseAddress" htmlFor="differentaddress"><span>Ship to a
                                                    different address?</span></label>
                                        </div>
                                    </div>
                                </div>
                                <div id="collapseAddress" className="different_address collapse in">
                                    <div className="row">
                                        <div className="form-group col-lg-6">
                                            <input type="text" required="" name="fname" placeholder="First name *"/>
                                        </div>
                                        <div className="form-group col-lg-6">
                                            <input type="text" required="" name="lname" placeholder="Last name *"/>
                                        </div>
                                    </div>
                                    <div className="row shipping_calculator">
                                        <div className="form-group col-lg-6">
                                            <input required="" type="text" name="cname" placeholder="Company Name"/>
                                        </div>
                                        <div className="form-group col-lg-6">
                                            <div className="custom_select w-100">
                                                <Select
                                                    className="checkout-react-select"
                                                    classNamePrefix="checkout-select"
                                                    options={countryOptions}
                                                    isSearchable
                                                    placeholder="Select an option..."
                                                    value={countryOptions.find((option) => option.value === formData.shipping_country) || null}
                                                    onChange={(selectedOption) => handleSelectChange('shipping_country', selectedOption)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="form-group col-lg-6">
                                            <input type="text" name="billing_address" required=""
                                                placeholder="Address *"/>
                                        </div>
                                        <div className="form-group col-lg-6">
                                            <input type="text" name="billing_address2" required=""
                                                placeholder="Address line2"/>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="form-group col-lg-6">
                                            <input required="" type="text" name="state" placeholder="State / County *"/>
                                        </div>
                                        <div className="form-group col-lg-6">
                                            <input required="" type="text" name="city" placeholder="City / Town *"/>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="form-group col-lg-6">
                                            <input required="" type="text" name="zipcode"
                                                placeholder="Postcode / ZIP *"/>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
                <div className="col-lg-5">
                    <div className="border p-40 cart-totals ml-30 mb-50">
                        <div className="d-flex align-items-end justify-content-between mb-30">
                            <h4>Your Order</h4>
                            <h6 className="text-muted">Subtotal</h6>
                        </div>
                        <div className="divider-2 mb-30"></div>
                        <div className="table-responsive order_table checkout">
                            <table className="table no-border">
                                <tbody>
                                    {cartItems.map((item) => (
                                      <tr key={item.ProductId}>
                                        <td className="image product-thumbnail"><ProductImage src={item.DefaultImage} alt={item.ProductName} /></td>
                                        <td>
                                            <h6 className="w-160 mb-5"><Link to={`/product-detail/${item.ProductId}`}
                                                    className="text-heading">{item.ProductName}</Link></h6>
                                            <div className="product-rate-cover">
                                                <div className="product-rate d-inline-block">
                                                    <div className="product-rating" style={{width: '90%'}}>
                                                    </div>
                                                </div>
                                                <span className="font-small ml-5 text-muted"> ({item.Rating || 0})</span>
                                            </div>
                                        </td>
                                        <td>
                                            <h6 className="text-muted pl-20 pr-20">x {item.quantity}</h6>
                                        </td>
                                        <td>
                                            <h4 className="text-brand"><i className="fa fa-inr fs-10" aria-hidden="true"></i>{formatCurrency(Number(item.Price || 0) * Number(item.quantity || 0))}</h4>
                                        </td>
                                      </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="divider-2 mt-20 mb-20"></div>
                        <div className="d-flex align-items-center justify-content-between">
                            <h6 className="text-muted mb-0">Total</h6>
                            <h4 className="text-brand mb-0"><i className="fa fa-inr fs-10" aria-hidden="true"></i>{formatCurrency(cartTotal)}</h4>
                        </div>
                    </div>
                    <div className="payment ml-30">
                        <h4 className="mb-30">Payment</h4>
                        <div className="payment_option">
                            <div className="custome-radio">
                                <input className="form-check-input" required type="radio" name="payment_option"
                                    id="exampleRadios3" value="bank-transfer" checked={paymentOption === 'bank-transfer'} onChange={(event) => setPaymentOption(event.target.value)} />
                                <label className="form-check-label" htmlFor="exampleRadios3" data-bs-toggle="collapse"
                                    data-target="#bankTranfer" aria-controls="bankTranfer">Direct Bank Transfer</label>
                            </div>
                            <div className="custome-radio">
                                <input className="form-check-input" required type="radio" name="payment_option"
                                    id="exampleRadios4" value="cod" checked={paymentOption === 'cod'} onChange={(event) => setPaymentOption(event.target.value)} />
                                <label className="form-check-label" htmlFor="exampleRadios4" data-bs-toggle="collapse"
                                    data-target="#checkPayment" aria-controls="checkPayment">Cash on delivery</label>
                            </div>
                            <div className="custome-radio">
                                <input className="form-check-input" required type="radio" name="payment_option"
                                    id="exampleRadios5" value="online" checked={paymentOption === 'online'} onChange={(event) => setPaymentOption(event.target.value)} />
                                <label className="form-check-label" htmlFor="exampleRadios5" data-bs-toggle="collapse"
                                    data-target="#paypal" aria-controls="paypal">Online Getway</label>
                            </div>
                        </div>
                        <div className="payment-logo d-flex">
                            <img className="mr-15" src="assets/imgs/theme/icons/payment-paypal.svg" alt=""/>
                            <img className="mr-15" src="assets/imgs/theme/icons/payment-visa.svg" alt=""/>
                            <img className="mr-15" src="assets/imgs/theme/icons/payment-master.svg" alt=""/>
                            <img src="assets/imgs/theme/icons/payment-zapper.svg" alt=""/>
                        </div>
                        {formError ? <p className="text-danger mt-15 mb-0">{formError}</p> : null}
                        <button type="submit" form="checkout-form" className="btn btn-fill-out btn-block mt-30" disabled={isSubmitting}>{isSubmitting ? 'Placing Order...' : 'Place an Order'}<i
                                className="fi-rs-sign-out ml-15"></i></button>
                    </div>
                </div>
            </div>
        </div>
  );
};

export default ShopCheckout;
